import { useState } from "react";
import Link from "next/link";
import classNames from "classnames/bind";
import { Size as SizeType } from "./types";
import Size from "@JohnLewisPartnership/jl-design-system/dist/components/size/Size";
import { MinusOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/MinusOutlined";
import { PlusOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/PlusOutlined";
import { TertiaryButton } from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";
import styles from "./Sizes.module.scss";
import { filterQueryParams } from "@/utils/url/filterQueryParams";
import type { ParsedUrlQuery } from "querystring";
import type { UrlObject } from "url";

const cx = classNames.bind(styles);

type SizeItemsProps = {
  sizes: SizeType[];
  showMore?: number;
  groupName?: string;
  replaceHistoryEntryOnVariantChange: boolean;
};

export const SizeItems = ({
  sizes,
  showMore,
  groupName,
  replaceHistoryEntryOnVariantChange,
}: SizeItemsProps) => {
  const [isOpen, setOpen] = useState(false);
  const visibleSizes = sizes.filter(
    (size, index) =>
      isOpen || size.isSelected || (showMore && index < showMore) || !showMore,
  );

  const hiddenItems = sizes.length - visibleSizes.length;

  const isButtonVisible = !!showMore && ((!isOpen && !!hiddenItems) || isOpen);

  return (
    <li className={cx("group", { reducedMargin: !isButtonVisible })}>
      {groupName && <h5 className={styles.heading}>{groupName}</h5>}
      <ul className={styles.groupSizesList} data-testid="group:sizes:list">
        {visibleSizes.map((size, index) => {
          const sanitizedHref = sanitizeHref(size.url);

          return (
            <Size
              className={styles.size}
              key={`size-${index}`}
              id={`size-${size.name}`}
              data-testid="size:option"
              unavailable={size.unavailable}
              value={size.name}
              selected={size.isSelected}
              hasReducedPrice={size.showReduced}
              reducedPriceText={size.sizeReductionLabel}
              minimiseLabelPadding={true}
              Tag={Link}
              buttonProps={{
                href: sanitizedHref,
                shallow: true,
                scroll: false,
                replace: replaceHistoryEntryOnVariantChange,
              }}
            >
              {size.name}
            </Size>
          );
        })}
      </ul>
      {!!showMore && ((!isOpen && !!hiddenItems) || isOpen) && (
        <TertiaryButton
          small={true}
          onClick={() => setOpen((toggle) => !toggle)}
          IconComponent={
            isOpen ? (
              <MinusOutlined size="medium" />
            ) : (
              <PlusOutlined size="medium" />
            )
          }
          className={styles.showMore}
          iconPositionRight
        >
          {isOpen ? "Show less" : `Show ${hiddenItems} more`}
        </TertiaryButton>
      )}
    </li>
  );
};

export function sanitizeHref(href: UrlObject): UrlObject {
  const query = isParsedUrlQuery(href.query)
    ? filterQueryParams(href.query)
    : href.query;

  return {
    ...href,
    query,
  };
}

function isParsedUrlQuery(query: UrlObject["query"]): query is ParsedUrlQuery {
  return typeof query === "object" && query !== null;
}
